import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Box,
  CircularProgress,
  Tooltip,
  TextField
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import SearchIcon from '@material-ui/icons/Search'
import MapIcon from '@material-ui/icons/Map'
import LaunchIcon from '@material-ui/icons/Launch'
import TableChartIcon from '@material-ui/icons/TableChart'
import ListIcon from '@material-ui/icons/List'

import TopRail from '../DashboardV2/TopRail'
import { PropertyDetailDrawer, PropertyEditModal, saveBillingGroup, getPropertyRoute } from '../../PropertyDetail'
import { applyBasemapProvider, googleHybridProvider } from '../../HerbicideSchedule/v3/maricopaBasemap'
import '../../../styles/hmiReskin.css'
import '../DashboardV2/dashboardV2.css'
import './propertyOverview.css'

const V4_T = {
  green: '#1f5e3a',
  rust: '#a9341d',
  amber: '#d8973a',
  plum: '#6e3fb7',
  blue: '#3454c1',
  ink: '#1c2419',
  mute: '#6f7a6b',
  line: '#e7dfc9',
  cream: '#f4efe4',
  surface: '#fbf8f1'
}

const TABLE_PAGE_SIZES = [25, 50, 100, 500]
const LIST_BATCH_SIZE = 25

// Helpers
const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '-'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

const formatCompactCurrency = (value) => {
  const num = Number(value) || 0
  if (Math.abs(num) >= 1000000) return `$${(num / 1000000).toFixed(1)}M`
  if (Math.abs(num) >= 1000) return `$${(num / 1000).toFixed(1)}k`
  return formatCurrency(num)
}

// Short month-day label, e.g. "May 12". Null/invalid -> null.
const formatShortDate = (value) => {
  if (!value) return null
  const date = new Date(value)
  if (isNaN(date.getTime())) return null
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// Compact square-footage label, e.g. "245k SF" / "3.2M SF". Null/invalid -> null.
const formatCompactSF = (value) => {
  const num = Number(value)
  if (!Number.isFinite(num) || num <= 0) return null
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M SF`
  if (num >= 1000) return `${Math.round(num / 1000)}k SF`
  return `${Math.round(num)} SF`
}

// Relative age in days, e.g. "34d ago" / "today". Null/invalid -> null.
const formatRelativeAge = (value) => {
  if (!value) return null
  const date = new Date(value)
  if (isNaN(date.getTime())) return null
  const days = Math.floor((Date.now() - date.getTime()) / 86400000)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 365) return `${days}d ago`
  return `${Math.floor(days / 365)}y ago`
}

function Sparkline ({ values, width = 60, height = 20, color = V4_T.green, fill = true }) {
  const cleanValues = values && values.length ? values.map(v => Number(v) || 0) : [0, 0, 0, 0]
  const yMin = Math.min(...cleanValues)
  const yMax = Math.max(...cleanValues)
  const range = yMax - yMin || 1
  const xStep = cleanValues.length > 1 ? (width - 2) / (cleanValues.length - 1) : width - 2
  const points = cleanValues.map((value, index) => [
    1 + index * xStep,
    height - 2 - ((value - yMin) / range) * (height - 4)
  ])
  const path = points.map((point, index) => `${index ? 'L' : 'M'}${point[0].toFixed(1)} ${point[1].toFixed(1)}`).join(' ')
  const fillPath = `${path} L ${points[points.length - 1][0].toFixed(1)} ${height - 1} L ${points[0][0].toFixed(1)} ${height - 1} Z`

  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      {fill && <path d={fillPath} fill={color} opacity="0.12" />}
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={points[points.length - 1][0]} cy={points[points.length - 1][1]} r="2" fill={color} stroke="#fff" strokeWidth="1" />
    </svg>
  )
}

function PropertyOverview(props) {
  const { url, user, employees, tasks } = props

  const [loading, setLoading] = useState(true)
  const [properties, setProperties] = useState([])
  const [viewMode, setViewMode] = useState('table') // 'table', 'list', 'map'
  const [searchTerm, setSearchTerm] = useState('')
  const [routeFilter, setRouteFilter] = useState('all')
  const [companyFilter, setCompanyFilter] = useState('')
  const [attentionOpen, setAttentionOpen] = useState(false)
  const [activeFilter, setActiveFilter] = useState(null)
  const [selectedPropertyId, setSelectedPropertyId] = useState(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  
  const [order, setOrder] = useState('desc')
  const [orderBy, setOrderBy] = useState('netProfit')
  const [tablePage, setTablePage] = useState(0)
  const [tableRowsPerPage, setTableRowsPerPage] = useState(25)
  const [listVisibleCount, setListVisibleCount] = useState(LIST_BATCH_SIZE)
  const listScrollRef = useRef(null)
  const listSentinelRef = useRef(null)

  const fetchProperties = useCallback(async () => {
    setLoading(true)
    try {
      const res = await axios.get(`${url}/api/aspire/routing/properties`)
      setProperties(res.data)
    } catch (err) {
      console.error('Failed to fetch properties:', err)
    } finally {
      setLoading(false)
    }
  }, [url])

  useEffect(() => {
    fetchProperties()
  }, [fetchProperties])

  // Stats Calculations
  const filteredProperties = useMemo(() => {
    let filtered = properties.filter(p =>
      p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.address1?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(p.propertyId).includes(searchTerm)
    )

    if (routeFilter !== 'all') {
      filtered = filtered.filter(p => getPropertyRoute(p) === routeFilter)
    }

    if (companyFilter) {
      filtered = filtered.filter(p => String(p.companyId || '') === companyFilter)
    }

    if (activeFilter === 'losing') filtered = filtered.filter(p => p.netProfit < 0)
    if (activeFilter === 'underbid') filtered = filtered.filter(p => (p.estimationVariance || 0) < 0)
    if (activeFilter === 'ot') filtered = filtered.filter(p => (p.totalOTHours || 0) > 0)
    // Add other filters as needed

    const DATE_FIELDS = ['lastMaintenanceVisitDate', 'lastHerbicideVisitDate', 'lastIrrigationVisitDate']

    return filtered.sort((a, b) => {
      let aVal = a[orderBy]
      let bVal = b[orderBy]

      // Handle special cases — "crew" column now sorts by route (route-first).
      if (orderBy === 'crew') {
        aVal = getPropertyRoute(a) || ''
        bVal = getPropertyRoute(b) || ''
      }

      // Company: coalesce missing names so properties without a company sort last
      // (and adjacent rows of the same company stay grouped together).
      if (orderBy === 'companyName') {
        aVal = a.companyName || ''
        bVal = b.companyName || ''
      }

      // Dates: compare by timestamp; properties without a date always sort last.
      if (DATE_FIELDS.includes(orderBy)) {
        const aTime = aVal ? new Date(aVal).getTime() : null
        const bTime = bVal ? new Date(bVal).getTime() : null
        if (aTime === null && bTime === null) return 0
        if (aTime === null) return 1
        if (bTime === null) return -1
        return order === 'asc' ? aTime - bTime : bTime - aTime
      }

      if (typeof aVal === 'string') aVal = aVal.toLowerCase()
      if (typeof bVal === 'string') bVal = bVal.toLowerCase()

      if (order === 'asc') {
        return aVal < bVal ? -1 : 1
      } else {
        return aVal > bVal ? -1 : 1
      }
    })
  }, [properties, searchTerm, routeFilter, companyFilter, activeFilter, orderBy, order])

  // Stats reflect the ACTIVE FILTER (search / route / company / chips), so the
  // aggregate row reads the slice you're looking at — not the whole portfolio.
  const stats = useMemo(() => {
    const src = filteredProperties
    const total = src.length
    const healthy = src.filter(p => p.profitMargin >= 30).length
    const watch = total - healthy
    const losingMoney = src.filter(p => p.netProfit < 0)
    const losingCount = losingMoney.length
    const negativeNetValue = losingMoney.reduce((sum, p) => sum + (p.netProfit || 0), 0)

    const underBid = src.filter(p => (p.estimationVariance || 0) < 0)
    const underBidCount = underBid.length
    const hoursOverEstimate = Math.abs(underBid.reduce((sum, p) => sum + (p.estimationVariance || 0), 0))

    const totalOT = src.reduce((sum, p) => sum + (p.totalOTHours || 0), 0)
    const totalRev = src.reduce((sum, p) => sum + (p.totalEarnedRevenue || 0), 0)
    const totalNet = src.reduce((sum, p) => sum + (p.netProfit || 0), 0)
    const blendedMargin = totalRev > 0 ? (totalNet / totalRev) * 100 : 0

    const herbFlags = src.filter(p => p.herbicideFutureVisits === 0 && p.targetAnnualVisits > 0).length // Mock logic
    const irrigFlags = src.filter(p => (p.syncHealth?.issues || []).includes('no-board-visits')).length

    return {
      total, healthy, watch,
      losingCount, negativeNetValue,
      underBidCount, hoursOverEstimate,
      totalOT, herbFlags, irrigFlags,
      totalRev, totalNet, blendedMargin
    }
  }, [filteredProperties])

  // Needs-Attention always scans the FULL portfolio (the worst losers overall),
  // independent of the active filter.
  const attentionProperties = useMemo(() => {
    return [...properties]
      .sort((a, b) => (a.netProfit || 0) - (b.netProfit || 0))
      .slice(0, 4)
  }, [properties])

  useEffect(() => {
    setTablePage(0)
    setListVisibleCount(LIST_BATCH_SIZE)
  }, [searchTerm, routeFilter, companyFilter, activeFilter, orderBy, order])

  useEffect(() => {
    setTablePage(prev => {
      const maxPage = Math.max(0, Math.ceil(filteredProperties.length / tableRowsPerPage) - 1)
      return Math.min(prev, maxPage)
    })
  }, [filteredProperties.length, tableRowsPerPage])

  const paginatedProperties = useMemo(() => {
    const start = tablePage * tableRowsPerPage
    return filteredProperties.slice(start, start + tableRowsPerPage)
  }, [filteredProperties, tablePage, tableRowsPerPage])

  const displayedListProperties = useMemo(() => {
    return filteredProperties.slice(0, listVisibleCount)
  }, [filteredProperties, listVisibleCount])

  const listHasMore = listVisibleCount < filteredProperties.length

  useEffect(() => {
    if (viewMode !== 'list' || loading || !listHasMore) return undefined
    const root = listScrollRef.current
    const sentinel = listSentinelRef.current
    if (!root || !sentinel || typeof IntersectionObserver === 'undefined') return undefined

    const observer = new IntersectionObserver(entries => {
      if (!entries[0]?.isIntersecting) return
      setListVisibleCount(prev => Math.min(prev + LIST_BATCH_SIZE, filteredProperties.length))
    }, { root, rootMargin: '160px 0px', threshold: 0.01 })

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [viewMode, loading, listHasMore, filteredProperties.length])

  const selectedProperty = useMemo(
    () => properties.find(p => String(p.propertyId) === String(selectedPropertyId)) || null,
    [properties, selectedPropertyId]
  )

  const editCrews = useMemo(() => {
    const names = properties.map(p => p.primaryCrewLeader?.name).filter(Boolean)
    return [...new Set(names)].sort().map(name => ({ _id: name, name }))
  }, [properties])

  const handleRowClick = (propertyId) => {
    setSelectedPropertyId(propertyId)
    setDrawerOpen(true)
  }

  const handleSort = (id) => {
    setOrderBy(id)
    setOrder(order === 'asc' ? 'desc' : 'asc')
  }

  const routes = useMemo(() => {
    const names = properties.map(getPropertyRoute).filter(Boolean)
    return ['all', ...new Set(names)].sort()
  }, [properties])

  // Company options for the autocomplete — mirrors RoutingPropertiesTable so the
  // two pages behave identically. Keyed by companyId, with a per-company count.
  const companyOptions = useMemo(() => {
    const byId = new Map()
    properties.forEach(p => {
      const key = p.companyId ? String(p.companyId) : ''
      if (!key) return
      if (!byId.has(key)) {
        byId.set(key, {
          id: key,
          name: p.companyName || `Company #${key}`,
          label: `${p.companyName || 'Company'} #${key}`,
          count: 0
        })
      }
      byId.get(key).count += 1
    })
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name))
  }, [properties])

  // Searchable option list for the billing-group member picker.
  const billingPropertyOptions = useMemo(() => {
    const byId = new Map()
    properties.forEach(p => {
      const id = Number(p.aspirePropertyId || p.propertyId || p.PropertyID)
      if (!Number.isFinite(id) || id <= 0 || byId.has(id)) return
      byId.set(id, {
        propertyId: id,
        label: p.propertyName || p.name || p.companyName || `Property #${id}`,
        companyName: p.companyName || '',
        address: p.address1 || ''
      })
    })
    return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label))
  }, [properties])

  const selectedCompany = companyFilter
    ? companyOptions.find(c => c.id === companyFilter)
    : null

  // Persists the editable property fields the territory-override PATCH accepts
  // (prescribed man-hours + mowing flag). Other modal fields (crew lock, service
  // frequencies, irrigation tech) are not yet wired server-side and are ignored.
  const handleSavePropertyEdit = useCallback(async (payload, opts = {}) => {
    const aspirePropertyId = selectedProperty?.propertyId
    if (aspirePropertyId != null && payload) {
      const body = {}
      if (Object.prototype.hasOwnProperty.call(payload, 'mowingEnabled')) {
        body.mowingEnabled = !!payload.mowingEnabled
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'prescribedManHours')) {
        // null clears the prescription; a positive number sets it.
        body.prescribedManHours = payload.prescribedManHours
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'setupChecklist')) {
        // { geofenceSet, propertyLinesVerified, takeoffComplete } confirm flags.
        // Server stamps confirmedAt/confirmedBy + writes the Jobsite. takeoffComplete
        // drives the green ✓ (complete) vs amber Site Data state.
        body.setupChecklist = payload.setupChecklist
      }
      if (Object.keys(body).length > 0) {
        try {
          await axios.patch(`${url}/api/hmi/territory/properties/${aspirePropertyId}`, body)
          // Optimistically reflect saved fields without a full refetch round-trip.
          // setupChecklist is nested on the Jobsite server-side; the Site Data cell
          // reads p.siteTakeoff.complete + p.setupChecklist, so mirror the confirmed
          // flags onto both rather than spreading the raw PATCH body.
          setProperties(prev => prev.map(p => {
            if (String(p.propertyId) !== String(aspirePropertyId)) return p
            const next = { ...p }
            if (Object.prototype.hasOwnProperty.call(body, 'mowingEnabled')) next.mowingEnabled = body.mowingEnabled
            if (Object.prototype.hasOwnProperty.call(body, 'prescribedManHours')) next.prescribedManHours = body.prescribedManHours
            if (body.setupChecklist) {
              const sc = body.setupChecklist
              next.setupChecklist = {
                geofenceSet: { ...(p.setupChecklist?.geofenceSet || {}), confirmed: !!sc.geofenceSet?.confirmed },
                propertyLinesVerified: { ...(p.setupChecklist?.propertyLinesVerified || {}), confirmed: !!sc.propertyLinesVerified?.confirmed },
                takeoffComplete: { ...(p.setupChecklist?.takeoffComplete || {}), confirmed: !!sc.takeoffComplete?.confirmed, notes: sc.takeoffComplete?.notes || null }
              }
              next.siteTakeoff = {
                ...(p.siteTakeoff || { totalSF: 0, areaCount: 0 }),
                complete: !!sc.takeoffComplete?.confirmed,
                notes: sc.takeoffComplete?.notes || null
              }
            }
            return next
          }))
        } catch (err) {
          console.error('Failed to save property edit:', err)
        }
      }
      // Billing group is created/updated through its own endpoint. Errors are
      // re-thrown so the modal can show them inline (e.g. overlap conflict);
      // the modal owns closing on success.
      if (payload.billingGroup) {
        const res = await saveBillingGroup(axios, url, payload.billingGroup, opts.force)
        const billingGroup = res.data || null
        setProperties(prev => prev.map(p =>
          String(p.propertyId) === String(aspirePropertyId) ? { ...p, billingGroup } : p
        ))
      }
    }
  }, [url, selectedProperty])

  return (
    <div className="property-overview-root">
      <TopRail url={url} user={user} employees={employees} tasks={tasks} />
      
      <div className="property-overview-content">
        <header className="property-header property-header-compact">
          <div className="property-header-controls">
            <select
              className="property-crew-select"
              value={routeFilter}
              onChange={(e) => setRouteFilter(e.target.value)}
            >
              <option value="all">All routes</option>
              {routes.filter(r => r !== 'all').map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>

            <div className="property-view-toggle">
              <button
                type="button"
                className={`property-view-seg ${viewMode === 'table' ? 'active' : ''}`}
                onClick={() => setViewMode('table')}
              >
                <TableChartIcon fontSize="small" /> Table
              </button>
              <button
                type="button"
                className={`property-view-seg ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
              >
                <ListIcon fontSize="small" /> List
              </button>
              <button
                type="button"
                className={`property-view-seg ${viewMode === 'map' ? 'active' : ''}`}
                onClick={() => setViewMode('map')}
              >
                <MapIcon fontSize="small" /> Map
              </button>
            </div>
          </div>
        </header>

        <div className="stat-scope-label">
          {(searchTerm || routeFilter !== 'all' || companyFilter || activeFilter)
            ? `Filtered · ${filteredProperties.length} of ${properties.length} properties`
            : `All ${properties.length} properties`}
        </div>
        <section className="stat-cards-grid">
          <StatCard label="Properties" value={stats.total} sub={`${stats.healthy} healthy · ${stats.watch} watch`} />
          <StatCard label="Losing Money" value={stats.losingCount} sub={`${formatCompactCurrency(stats.negativeNetValue)} net YTD`} />
          <StatCard label="Under-Bid" value={stats.underBidCount} sub={`${stats.hoursOverEstimate.toFixed(0)}h over estimate`} />
          <StatCard label="Overtime" value={`${stats.totalOT.toFixed(0)}h`} sub="YTD across territory" />
          <StatCard label="Herb / Irrig Flags" value={stats.herbFlags + stats.irrigFlags} sub={`${stats.herbFlags} herbicide · ${stats.irrigFlags} irrigation`} />
          <StatCard label="Revenue" value={formatCompactCurrency(stats.totalRev)} sub="total customer value" />
          <StatCard label="Net Profit" value={formatCompactCurrency(stats.totalNet)} sub={`${stats.blendedMargin.toFixed(1)}% blended margin`} />
        </section>

        <section className="needs-attention-section">
          <button
            type="button"
            className={`attention-toggle ${attentionOpen ? 'open' : ''}`}
            onClick={() => setAttentionOpen(o => !o)}
            aria-expanded={attentionOpen}
          >
            <span className="attention-caret">{attentionOpen ? '▾' : '▸'}</span>
            <span className="section-label">Needs Attention</span>
            <span className="attention-count">{attentionProperties.length}</span>
          </button>
          {attentionOpen && (
            <div className="attention-cards-scroll">
              {attentionProperties.map(p => (
                <div key={p.propertyId} className="attention-card" onClick={() => handleRowClick(p.propertyId)} style={{ cursor: 'pointer' }}>
                  <div className="attention-card-header">
                    <span className="attention-card-title">{p.name}</span>
                    <span className="attention-card-value">{formatCurrency(p.netProfit)}</span>
                  </div>
                  <div className="attention-card-desc">
                    Losing {formatCurrency(Math.abs(p.netProfit))} a year — actual hours run {(p.avgOnSiteHours / (p.estimatedHours || 1)).toFixed(1)}x estimate
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="filter-chips-row">
          <FilterChip label={`Losing money ${stats.losingCount}`} active={activeFilter === 'losing'} onClick={() => setActiveFilter(activeFilter === 'losing' ? null : 'losing')} />
          <FilterChip label={`Under-bid ${stats.underBidCount}`} active={activeFilter === 'underbid'} onClick={() => setActiveFilter(activeFilter === 'underbid' ? null : 'underbid')} />
          <FilterChip label={`OT offenders ${properties.filter(p => p.totalOTHours > 0).length}`} active={activeFilter === 'ot'} onClick={() => setActiveFilter(activeFilter === 'ot' ? null : 'ot')} />

          <Box flex={1} />

          <span className="property-count-label">
            {filteredProperties.length} of {properties.length} properties
          </span>
        </div>

        <div className="property-table-search-row">
          <div className="property-search-field">
            <SearchIcon fontSize="small" />
            <input
              className="property-search-input"
              type="text"
              placeholder="Search properties by name or address..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <Autocomplete
            size="small"
            options={companyOptions}
            value={selectedCompany || null}
            onChange={(_event, option) => setCompanyFilter(option?.id || '')}
            getOptionLabel={(option) => option ? `${option.label} (${option.count})` : ''}
            getOptionSelected={(option, value) => option.id === value.id}
            className="property-company-filter"
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Filter by company..."
              />
            )}
          />
        </div>

        <div className="property-table-container">
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={8}>
              <CircularProgress color="primary" />
            </Box>
          ) : viewMode === 'map' ? (
            <div className="property-map-view">
              <PropertyMap properties={filteredProperties} onSelectProperty={handleRowClick} />
            </div>
          ) : viewMode === 'list' ? (
            <div className="property-list-view" ref={listScrollRef}>
              {filteredProperties.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#97a094' }}>No properties match the current filters.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
                  <ListSection 
                    title="Losing Money" 
                    count={displayedListProperties.filter(p => p.netProfit < 0).length}
                    properties={displayedListProperties.filter(p => p.netProfit < 0)}
                    onSelect={handleRowClick}
                  />
                  <ListSection 
                    title="Under-Bid" 
                    count={displayedListProperties.filter(p => p.netProfit >= 0 && (p.estimationVariance || 0) < 0).length}
                    properties={displayedListProperties.filter(p => p.netProfit >= 0 && (p.estimationVariance || 0) < 0)}
                    onSelect={handleRowClick}
                  />
                  <ListSection 
                    title="Healthy" 
                    count={displayedListProperties.filter(p => p.netProfit >= 0 && (p.estimationVariance || 0) >= 0).length}
                    properties={displayedListProperties.filter(p => p.netProfit >= 0 && (p.estimationVariance || 0) >= 0)}
                    onSelect={handleRowClick}
                  />
                  <div ref={listSentinelRef} className="property-list-sentinel">
                    {listHasMore ? (
                      <button
                        type="button"
                        className="property-load-more"
                        onClick={() => setListVisibleCount(prev => Math.min(prev + LIST_BATCH_SIZE, filteredProperties.length))}
                      >
                        Load more
                      </button>
                    ) : (
                      <span>Showing {displayedListProperties.length} of {filteredProperties.length}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="property-table-scroll">
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ width: 40 }}></TableCell>
                      <SortHeader label="Property" id="name" current={orderBy} order={order} onSort={handleSort} />
                      <SortHeader label="Company" id="companyName" current={orderBy} order={order} onSort={handleSort} />
                      <SortHeader label="Route · Day" id="crew" current={orderBy} order={order} onSort={handleSort} />
                      <TableCell>Services</TableCell>
                      <TableCell>Programs</TableCell>
                      <TableCell>Site Data</TableCell>
                      <SortHeader label="Visits" id="completedOccurrences" current={orderBy} order={order} onSort={handleSort} />
                      <SortHeader label="Hrs / Visit" id="avgOnSiteHours" current={orderBy} order={order} onSort={handleSort} />
                      <SortHeader label="OT" id="totalOTHours" current={orderBy} order={order} onSort={handleSort} />
                      <SortHeader label="Last Service" id="lastMaintenanceVisitDate" current={orderBy} order={order} onSort={handleSort} />
                      <SortHeader label="Last Herbicide" id="lastHerbicideVisitDate" current={orderBy} order={order} onSort={handleSort} />
                      <SortHeader label="Last Irrig Check" id="lastIrrigationVisitDate" current={orderBy} order={order} onSort={handleSort} />
                      <SortHeader label="Revenue" id="totalEarnedRevenue" current={orderBy} order={order} onSort={handleSort} />
                      <SortHeader label="Gross" id="netProfit" current={orderBy} order={order} onSort={handleSort} />
                      <SortHeader label="Margin · 12 Mo" id="profitMargin" current={orderBy} order={order} onSort={handleSort} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedProperties.map(p => {
                  const marginColor = p.profitMargin < 30 ? V4_T.rust : V4_T.green
                  const visitProgress = p.targetAnnualVisits > 0 ? (p.completedOccurrences / p.targetAnnualVisits) * 100 : 0
                  const hrDelta = (p.avgOnSiteHours || 0) - (p.estimatedHours || 0)
                  
                  return (
                    <TableRow key={p.propertyId} hover className="property-row" onClick={() => handleRowClick(p.propertyId)} style={{ cursor: 'pointer' }}>
                      <TableCell align="center">
                        <Tooltip title={p.profitMargin < 30 ? 'Unhealthy - Saguaro' : 'Healthy - Agave'}>
                          <span style={{ color: marginColor, fontSize: 18, cursor: 'help' }}>
                            {p.profitMargin < 30 ? '🌵' : '🌿'}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <div style={{ fontWeight: 800, color: V4_T.ink, fontSize: 13 }}>{p.name}</div>
                        <div style={{ color: V4_T.mute, fontSize: 11 }}>{p.address1} · #{p.propertyId}</div>
                      </TableCell>
                      <TableCell style={{ fontSize: 12, color: V4_T.ink, maxWidth: 200 }}>
                        {p.companyId
                          ? `${p.companyName || 'Company'} #${p.companyId}`
                          : (p.companyName || '-')}
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: V4_T.green }} />
                          <span style={{ fontWeight: 600, fontSize: 12 }}>{getPropertyRoute(p) || '-'}</span>
                          <span style={{ color: V4_T.mute, fontSize: 12 }}>· {p.scheduledDays?.[0]?.slice(0, 3) || '-'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="service-progress-bar">
                          <div className="service-progress-fill" style={{ width: `${Math.min(100, visitProgress)}%`, background: visitProgress > 100 ? V4_T.amber : V4_T.green }} />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {p.mowingEnabled && <span className="program-chip program-chip-m" title="Mowing program">M ✓</span>}
                          {p.herbicideCompletedVisits > 0 && <span className="program-chip program-chip-h" title={`${p.herbicideCompletedVisits} herbicide visits done`}>H ✓</span>}
                          {p.irrigationCompletedVisits > 0 && <span className="program-chip program-chip-i" title={`${p.irrigationCompletedVisits} irrigation visits done`}>I ✓</span>}
                        </div>
                      </TableCell>
                      <SiteDataCell property={p} />
                      <TableCell style={{ fontFamily: 'var(--f-mono)', fontSize: 12, fontWeight: 600 }}>
                        {p.completedOccurrences} / {p.targetAnnualVisits || 0}
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, fontFamily: 'var(--f-mono)', fontSize: 12 }}>
                          <span style={{ fontWeight: 800 }}>{(p.avgOnSiteHours || 0).toFixed(1)}</span>
                          <span style={{ color: V4_T.mute, fontSize: 10 }}>/ {(p.estimatedHours || 0).toFixed(1)}</span>
                          <span className={`delta-value ${hrDelta > 0 ? 'delta-pos' : 'delta-neg'}`}>
                            {hrDelta > 0 ? '+' : ''}{hrDelta.toFixed(1)}h
                          </span>
                        </div>
                      </TableCell>
                      <TableCell style={{ fontFamily: 'var(--f-mono)', fontSize: 12, color: (p.totalOTHours || 0) > 0 ? V4_T.rust : V4_T.mute }}>
                        {(p.totalOTHours || 0).toFixed(1)}h
                      </TableCell>
                      <LastVisitCell date={p.lastMaintenanceVisitDate} />
                      <LastVisitCell date={p.lastHerbicideVisitDate} />
                      <LastVisitCell date={p.lastIrrigationVisitDate} />
                      <TableCell style={{ fontFamily: 'var(--f-mono)', fontSize: 12, fontWeight: 600 }}>
                        {formatCompactCurrency(p.totalEarnedRevenue)}
                      </TableCell>
                      <Tooltip title={<GrossBreakdown property={p} />} arrow placement="left">
                        <TableCell style={{ fontFamily: 'var(--f-mono)', fontSize: 12, fontWeight: 800, color: (p.netProfit || 0) < 0 ? V4_T.rust : V4_T.green, cursor: 'help' }}>
                          {formatCurrency(p.netProfit)}
                        </TableCell>
                      </Tooltip>
                      <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontWeight: 800, color: marginColor, fontSize: 12, minWidth: 40, textAlign: 'right' }}>
                            {p.profitMargin?.toFixed(1)}%
                          </span>
                          <div className="sparkline-container">
                            {p.marginHistory?.length > 0 && (
                              <Sparkline values={p.marginHistory.map(m => m.margin)} color={marginColor} />
                            )}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
                    {paginatedProperties.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={16} align="center" style={{ padding: 32, color: V4_T.mute }}>
                          No properties match the current filters.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              <TablePaginationBar
                page={tablePage}
                rowsPerPage={tableRowsPerPage}
                total={filteredProperties.length}
                onPageChange={setTablePage}
                onRowsPerPageChange={(next) => {
                  setTableRowsPerPage(next)
                  setTablePage(0)
                }}
              />
            </>
          )}
        </div>
        
        <footer style={{ padding: '16px 0', fontSize: 11, color: '#97a094', display: 'flex', justifyContent: 'space-between' }}>
          <div>{stats.healthy} healthy · {stats.watch} watch · 8 critical</div>
          <div>synced from Aspire {user?.lastSyncTime || 'recently'}</div>
        </footer>
      </div>

      <PropertyDetailDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        property={selectedProperty}
        url={url}
        allRows={properties}
        onEditProperty={() => setEditOpen(true)}
      />

      <PropertyEditModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        property={selectedProperty}
        crews={editCrews}
        propertyOptions={billingPropertyOptions}
        onSave={handleSavePropertyEdit}
      />
    </div>
  )
}

function StatCard({ label, value, sub }) {
  return (
    <div className="property-stat-card">
      <div className="stat-card-label">{label}</div>
      <div className="stat-card-value">{value}</div>
      <div className="stat-card-sub">{sub}</div>
    </div>
  )
}

// Tooltip body explaining the "Gross" column. Gross = earned revenue − actual
// labor cost; it excludes materials, equipment, subs and overhead, so it is
// gross-of-labor, not true net profit.
function GrossBreakdown({ property }) {
  const revenue = property.totalEarnedRevenue || 0
  const labor = property.totalLaborCost || property.maintenanceLaborCost || 0
  const gross = property.netProfit || 0
  const row = (label, value, opts = {}) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontWeight: opts.bold ? 800 : 500 }}>
      <span>{label}</span>
      <span style={{ fontFamily: 'var(--f-mono)' }}>{opts.sign || ''}{formatCurrency(Math.abs(value))}</span>
    </div>
  )
  return (
    <div style={{ fontSize: 12, lineHeight: 1.7, minWidth: 200, padding: '2px 0' }}>
      {row('Earned Revenue', revenue)}
      {row('Labor Cost', labor, { sign: '− ' })}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.3)', margin: '4px 0' }} />
      {row('Gross', gross, { bold: true })}
      <div style={{ marginTop: 6, opacity: 0.75, fontSize: 10.5 }}>
        Revenue minus labor only. Excludes materials, equipment, subs &amp; overhead.
      </div>
    </div>
  )
}

// Site Data cell: takeoff / irrigation assets / prescribed hours at a glance.
// Expected (future) row fields: p.siteTakeoff = { complete, totalSF, areaCount },
// p.irrigationAssets = { zoneCount }; p.prescribedManHours already exists.
// Missing data renders as muted dashes, so rows light up as the API adds fields.
function SiteDataCell({ property }) {
  const takeoff = property.siteTakeoff || null
  const hasTakeoffData = !!takeoff && ((takeoff.totalSF || 0) > 0 || (takeoff.areaCount || 0) > 0)
  // "complete" is a manager sign-off on the Jobsite and can exist even before any
  // area geometry is drawn — so it drives the green ✓ independently of hasTakeoffData.
  const takeoffComplete = !!takeoff?.complete
  const hasTakeoffAny = hasTakeoffData || takeoffComplete
  const zoneCount = property.irrigationAssets?.zoneCount || 0
  const rxHours = Number(property.prescribedManHours)
  const hasRx = Number.isFinite(rxHours) && rxHours > 0

  const sfLabel = hasTakeoffData ? formatCompactSF(takeoff.totalSF) : null
  const areaLabel = hasTakeoffData && (takeoff.areaCount || 0) > 0
    ? `${takeoff.areaCount} area${takeoff.areaCount === 1 ? '' : 's'}`
    : null
  const takeoffSummary = [sfLabel, areaLabel].filter(Boolean).join(' · ')

  const seg = (key, label, present, color) => (
    <span key={key} style={{ display: 'inline-flex', alignItems: 'baseline', gap: 3, whiteSpace: 'nowrap' }}>
      <span style={{ fontWeight: 800, fontSize: 10, color: present ? color : '#b4bbae' }}>{key}</span>
      <span style={{ fontSize: 11, fontFamily: 'var(--f-mono)', fontWeight: present ? 700 : 400, color: present ? V4_T.ink : '#b4bbae' }}>
        {present ? label : '—'}
      </span>
    </span>
  )

  const tooltip = (
    <div style={{ fontSize: 12, lineHeight: 1.7, padding: '2px 0' }}>
      <div>
        Takeoff: {hasTakeoffAny
          ? `${takeoffComplete ? 'complete' : 'in progress'}${takeoffSummary ? ` — ${takeoffSummary}` : ''}`
          : 'no takeoff yet'}
      </div>
      <div>Irrigation: {zoneCount > 0 ? `${zoneCount} zone${zoneCount === 1 ? '' : 's'} mapped` : 'no asset data'}</div>
      <div>Prescribed hours: {hasRx ? `${rxHours.toFixed(1)} man-hrs / visit` : 'not prescribed'}</div>
    </div>
  )

  const jobsiteId = property.jobsiteId || null

  return (
    <TableCell style={{ cursor: 'help' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
        <Tooltip title={tooltip} arrow placement="left">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
            <div>
              {seg(
                takeoffComplete ? 'T ✓' : hasTakeoffData ? 'T ◐' : 'T',
                takeoffSummary || (takeoffComplete ? 'confirmed' : '✓'),
                hasTakeoffAny,
                takeoffComplete ? V4_T.green : V4_T.amber
              )}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              {seg(zoneCount > 0 ? 'I ✓' : 'I', `${zoneCount} zone${zoneCount === 1 ? '' : 's'}`, zoneCount > 0, V4_T.blue)}
              {seg('Rx', `${hasRx ? rxHours.toFixed(1) : ''}h`, hasRx, V4_T.plum)}
            </div>
          </div>
        </Tooltip>
        {jobsiteId && (
          <Tooltip title="Open in Takeoffs">
            <a
              href={`/site-takeoff/${jobsiteId}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{ color: V4_T.mute, display: 'inline-flex', marginTop: 1 }}
            >
              <LaunchIcon style={{ fontSize: 14 }} />
            </a>
          </Tooltip>
        )}
      </div>
    </TableCell>
  )
}

// Table cell showing a "last X visit" date with relative age beneath it.
// Empty/never -> em-dash + "never".
function LastVisitCell({ date }) {
  const label = formatShortDate(date)
  if (!label) {
    return (
      <TableCell style={{ fontSize: 12 }}>
        <div style={{ color: V4_T.mute }}>—</div>
        <div style={{ color: '#b4bbae', fontSize: 10 }}>never</div>
      </TableCell>
    )
  }
  return (
    <TableCell style={{ fontSize: 12 }}>
      <div style={{ fontWeight: 700, color: V4_T.ink }}>{label}</div>
      <div style={{ color: V4_T.mute, fontSize: 10 }}>{formatRelativeAge(date)}</div>
    </TableCell>
  )
}

function FilterChip({ label, active, onClick }) {
  return (
    <button className={`filter-chip ${active ? 'active' : ''}`} onClick={onClick}>
      {label}
    </button>
  )
}

function SortHeader({ label, id, current, order, onSort }) {
  const active = current === id
  return (
    <TableCell>
      <TableSortLabel
        active={active}
        direction={active ? order : 'asc'}
        onClick={() => onSort(id)}
        style={{ fontWeight: 800, fontSize: 11, textTransform: 'uppercase', color: '#97a094' }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  )
}

function TablePaginationBar ({ page, rowsPerPage, total, onPageChange, onRowsPerPageChange }) {
  const pageStart = total > 0 ? page * rowsPerPage + 1 : 0
  const pageEnd = Math.min((page + 1) * rowsPerPage, total)
  const canPrevious = page > 0
  const canNext = pageEnd < total

  return (
    <div className="property-pagination-bar">
      <span>Rows per page:</span>
      <select
        value={rowsPerPage}
        onChange={(event) => onRowsPerPageChange(Number(event.target.value))}
        className="property-pagination-select"
      >
        {TABLE_PAGE_SIZES.map(size => (
          <option key={size} value={size}>{size}</option>
        ))}
      </select>
      <span className="property-pagination-range">{pageStart}-{pageEnd} of {total}</span>
      <button
        type="button"
        disabled={!canPrevious}
        onClick={() => onPageChange(Math.max(0, page - 1))}
        className="property-pagination-button"
        aria-label="Previous page"
      >
        &lsaquo;
      </button>
      <button
        type="button"
        disabled={!canNext}
        onClick={() => onPageChange(page + 1)}
        className="property-pagination-button"
        aria-label="Next page"
      >
        &rsaquo;
      </button>
    </div>
  )
}

function PropertyMap({ properties, onSelectProperty }) {
  const mapRef = React.useRef(null)
  const tileLayerRef = React.useRef(null)
  
  useEffect(() => {
    if (!mapRef.current) {
      const map = L.map('property-overview-map').setView([33.4484, -112.0740], 10)
      applyBasemapProvider({ map, tileLayerRef, provider: googleHybridProvider })
      mapRef.current = map
    }
    
    const map = mapRef.current
    
    // Clear existing markers
    map.eachLayer((layer) => {
      if (layer instanceof L.Marker) {
        map.removeLayer(layer)
      }
    })
    
    const markers = []
    properties.forEach(p => {
      if (p.latitude && p.longitude) {
        const marginColor = (p.profitMargin || 0) < 30 ? V4_T.rust : V4_T.green
        const marker = L.circleMarker([p.latitude, p.longitude], {
          radius: 6,
          fillColor: marginColor,
          color: '#fff',
          weight: 2,
          opacity: 1,
          fillOpacity: 0.8
        })
          .bindPopup(`<b>${p.name}</b><br/>Margin: ${p.profitMargin?.toFixed(1)}%`)
          .on('click', () => onSelectProperty(p.propertyId))
          .addTo(map)
        markers.push(marker)
      }
    })
    
    if (markers.length > 0) {
      const group = new L.featureGroup(markers)
      map.fitBounds(group.getBounds().pad(0.1))
    }
  }, [properties, onSelectProperty])
  
  return <div id="property-overview-map" style={{ width: '100%', height: '100%', borderRadius: 12 }} />
}

function ListSection({ title, count, properties, onSelect }) {
  if (count === 0) return null
  return (
    <div className="list-section">
      <div className="section-label" style={{ marginBottom: 16 }}>{title} · {count}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
        {properties.map(p => (
          <div key={p.propertyId} className="attention-card" onClick={() => onSelect(p.propertyId)} style={{ cursor: 'pointer' }}>
            <div className="attention-card-header">
              <span className="attention-card-title">{p.name}</span>
              <span className="attention-card-value" style={{ color: p.netProfit < 0 ? V4_T.rust : V4_T.green }}>{formatCurrency(p.netProfit)}</span>
            </div>
            <div style={{ fontSize: 11, color: V4_T.mute, marginBottom: 8 }}>{p.address1}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: V4_T.green }} />
                <span style={{ fontWeight: 600, fontSize: 12 }}>{getPropertyRoute(p) || '-'}</span>
              </div>
              <div style={{ fontWeight: 800, color: p.profitMargin < 30 ? V4_T.rust : V4_T.green, fontSize: 13 }}>
                {p.profitMargin?.toFixed(1)}%
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user,
  employees: state.employees,
  tasks: state.tasks,
})

export default connect(mapStateToProps)(PropertyOverview)
